/**
 * Kunga Basics Admin — Legal & Support Pages
 * Privacy Policy · Terms of Service · Support
 * Fully internationalised: en · fr · kin
 */

import { useState } from "react";
import {
  ShieldCheck, FileText, LifeBuoy,
  ChevronDown, ChevronUp, Mail, MessageSquare,
  ExternalLink, BookOpen, CheckCircle2, AlertCircle,
  Clock, Globe, Lock, Database, Eye, UserCheck,
  Headphones, FileQuestion, Zap,
} from "lucide-react";
import { useLang } from "../lib/i18n.jsx";

// ─── SHARED HELPERS ───────────────────────────────────────────────────────────

function PageHero({ icon: Icon, color, title, subtitle }) {
  return (
    <div style={{
      background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: 16,
      padding: "32px 36px",
      marginBottom: 28,
      display: "flex",
      alignItems: "center",
      gap: 20,
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, boxShadow: `0 8px 20px ${color}40`,
      }}>
        <Icon size={26} color="#fff" />
      </div>
      <div>
        <h1 style={{ margin: 0, fontFamily: "var(--fd)", fontWeight: 900, fontSize: 24, color: "var(--ink)", lineHeight: 1.2 }}>
          {title}
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--ghost)", lineHeight: 1.5 }}>
          {subtitle}
        </p>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, color = "var(--teal)", children }) {
  return (
    <div style={{
      background: "var(--card)", borderRadius: 14, border: "1px solid var(--border)",
      overflow: "hidden", marginBottom: 16,
    }}>
      <div style={{
        padding: "16px 20px", borderBottom: "1px solid var(--border)",
        display: "flex", alignItems: "center", gap: 10,
        background: "var(--panel)",
      }}>
        {Icon && (
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: `${color}20`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon size={14} color={color} />
          </div>
        )}
        <span style={{ fontFamily: "var(--fd)", fontWeight: 800, fontSize: 14, color: "var(--ink)" }}>
          {title}
        </span>
      </div>
      <div style={{ padding: "18px 20px" }}>
        {children}
      </div>
    </div>
  );
}

function Para({ children }) {
  return (
    <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--ghost)", lineHeight: 1.7 }}>
      {children}
    </p>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ margin: "0 0 4px", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 13, color: "var(--ghost)", lineHeight: 1.6 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

function InfoBadge({ icon: Icon, label, value, color = "var(--teal)" }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", borderRadius: 10,
      background: `${color}12`, border: `1px solid ${color}25`,
    }}>
      <Icon size={15} color={color} />
      <div>
        <div style={{ fontSize: 10, color: color, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
        <div style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600, marginTop: 1 }}>{value}</div>
      </div>
    </div>
  );
}

function ContactBlock({ text }) {
  return (
    <div style={{
      padding: "14px 18px", borderRadius: 10, background: "var(--panel)",
      border: "1px solid var(--border)", fontFamily: "var(--fm)",
      fontSize: 13, color: "var(--ink)", lineHeight: 1.8,
      whiteSpace: "pre-line",
    }}>
      {text}
    </div>
  );
}

// ─── PRIVACY POLICY ───────────────────────────────────────────────────────────

export function PagePrivacyPolicy() {
  const { t } = useLang();
  return (
    <div style={{ padding: "28px 32px", maxWidth: 860, margin: "0 auto" }}>
      <PageHero
        icon={ShieldCheck}
        color="#0db87e"
        title={t("privacy.title")}
        subtitle={t("privacy.subtitle")}
      />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <InfoBadge icon={Clock}  label={t("legal.lastUpdated")}  value={t("legal.lastUpdatedVal")} color="var(--teal)" />
        <InfoBadge icon={Globe}  label={t("legal.jurisdiction")} value={t("legal.rwandaVal")}      color="var(--blue)" />
        <InfoBadge icon={Lock}   label={t("legal.dataStandard")} value={t("legal.gdprVal")}        color="var(--amber)" />
      </div>

      <Section title={t("privacy.s1.title")} icon={UserCheck} color="var(--teal)">
        <Para>{t("privacy.s1.p1")}</Para>
        <Para>{t("privacy.s1.p2")}</Para>
      </Section>

      <Section title={t("privacy.s2.title")} icon={Database} color="var(--blue)">
        <Para>{t("privacy.s2.lead")}</Para>
        <BulletList items={[
          t("privacy.s2.i1"), t("privacy.s2.i2"), t("privacy.s2.i3"),
          t("privacy.s2.i4"), t("privacy.s2.i5"),
        ]} />
      </Section>

      <Section title={t("privacy.s3.title")} icon={Eye} color="var(--amber)">
        <Para>{t("privacy.s3.lead")}</Para>
        <BulletList items={[
          t("privacy.s3.i1"), t("privacy.s3.i2"), t("privacy.s3.i3"),
          t("privacy.s3.i4"), t("privacy.s3.i5"),
        ]} />
        <Para style={{ marginTop: 12 }}>{t("privacy.s3.note")}</Para>
      </Section>

      <Section title={t("privacy.s4.title")} icon={Database} color="var(--teal)">
        <Para>{t("privacy.s4.p1")}</Para>
        <Para>{t("privacy.s4.p2")}</Para>
      </Section>

      <Section title={t("privacy.s5.title")} icon={CheckCircle2} color="var(--teal)">
        <Para>{t("privacy.s5.lead")}</Para>
        <BulletList items={[
          t("privacy.s5.i1"), t("privacy.s5.i2"), t("privacy.s5.i3"),
          t("privacy.s5.i4"), t("privacy.s5.i5"), t("privacy.s5.i6"),
        ]} />
      </Section>

      <Section title={t("privacy.s6.title")} icon={Lock} color="var(--rose)">
        <BulletList items={[
          t("privacy.s6.i1"), t("privacy.s6.i2"), t("privacy.s6.i3"),
          t("privacy.s6.i4"), t("privacy.s6.i5"),
        ]} />
      </Section>

      <Section title={t("privacy.s7.title")} icon={Mail} color="var(--blue)">
        <Para>{t("privacy.s7.p1")}</Para>
        <ContactBlock text={t("privacy.s7.contact")} />
      </Section>
    </div>
  );
}

// ─── TERMS OF SERVICE ─────────────────────────────────────────────────────────

export function PageTermsOfService() {
  const { t } = useLang();
  return (
    <div style={{ padding: "28px 32px", maxWidth: 860, margin: "0 auto" }}>
      <PageHero
        icon={FileText}
        color="#3b82f6"
        title={t("terms.title")}
        subtitle={t("terms.subtitle")}
      />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <InfoBadge icon={Clock}    label={t("legal.effectiveDate")} value={t("legal.lastUpdatedVal")} color="var(--blue)"  />
        <InfoBadge icon={Globe}    label={t("legal.governingLaw")}  value={t("legal.rwandaVal")}      color="var(--teal)"  />
        <InfoBadge icon={FileText} label={t("legal.version")}       value="v1.0"                      color="var(--amber)" />
      </div>

      <Section title={t("terms.s1.title")} icon={CheckCircle2} color="var(--blue)">
        <Para>{t("terms.s1.p1")}</Para>
        <Para>{t("terms.s1.p2")}</Para>
      </Section>

      <Section title={t("terms.s2.title")} icon={UserCheck} color="var(--teal)">
        <Para>{t("terms.s2.lead")}</Para>
        <BulletList items={[
          t("terms.s2.i1"), t("terms.s2.i2"), t("terms.s2.i3"),
          t("terms.s2.i4"), t("terms.s2.i5"), t("terms.s2.i6"),
        ]} />
      </Section>

      <Section title={t("terms.s3.title")} icon={CheckCircle2} color="var(--teal)">
        <Para>{t("terms.s3.lead")}</Para>
        <BulletList items={[
          t("terms.s3.i1"), t("terms.s3.i2"), t("terms.s3.i3"),
          t("terms.s3.i4"), t("terms.s3.i5"),
        ]} />
      </Section>

      <Section title={t("terms.s4.title")} icon={AlertCircle} color="var(--rose)">
        <Para>{t("terms.s4.lead")}</Para>
        <BulletList items={[
          t("terms.s4.i1"), t("terms.s4.i2"), t("terms.s4.i3"),
          t("terms.s4.i4"), t("terms.s4.i5"), t("terms.s4.i6"),
          t("terms.s4.i7"),
        ]} />
      </Section>

      <Section title={t("terms.s5.title")} icon={Lock} color="var(--amber)">
        <Para>{t("terms.s5.p1")}</Para>
        <Para>{t("terms.s5.p2")}</Para>
      </Section>

      <Section title={t("terms.s6.title")} icon={AlertCircle} color="var(--amber)">
        <Para>{t("terms.s6.p1")}</Para>
        <Para>{t("terms.s6.p2")}</Para>
      </Section>

      <Section title={t("terms.s7.title")} icon={AlertCircle} color="var(--rose)">
        <Para>{t("terms.s7.p1")}</Para>
        <Para>{t("terms.s7.p2")}</Para>
      </Section>

      <Section title={t("terms.s8.title")} icon={FileText} color="var(--blue)">
        <Para>{t("terms.s8.p1")}</Para>
      </Section>

      <Section title={t("terms.s9.title")} icon={Globe} color="var(--teal)">
        <Para>{t("terms.s9.p1")}</Para>
        <ContactBlock text={t("terms.s9.contact")} />
      </Section>
    </div>
  );
}

// ─── SUPPORT ──────────────────────────────────────────────────────────────────

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderRadius: 10, border: "1px solid var(--border)",
      overflow: "hidden", marginBottom: 8,
      borderColor: open ? "var(--teal)" : "var(--border)",
      transition: "border-color .15s",
    }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: "100%", background: open ? "rgba(13,184,126,.05)" : "var(--panel)",
          border: "none", cursor: "pointer",
          padding: "13px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          transition: "background .15s",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", textAlign: "left", lineHeight: 1.4 }}>{q}</span>
        {open
          ? <ChevronUp size={15} color="var(--teal)" style={{ flexShrink: 0 }} />
          : <ChevronDown size={15} color="var(--ghost)" style={{ flexShrink: 0 }} />
        }
      </button>
      {open && (
        <div style={{
          padding: "12px 16px 14px",
          borderTop: "1px solid var(--border)",
          fontSize: 13, color: "var(--ghost)", lineHeight: 1.7,
          background: "var(--card)",
        }}>
          {a}
        </div>
      )}
    </div>
  );
}

function ContactCard({ icon: Icon, title, value, sub, color, action, actionLabel }) {
  return (
    <div style={{
      background: "var(--card)", border: "1px solid var(--border)",
      borderRadius: 14, padding: "20px 22px",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontFamily: "var(--fd)", fontWeight: 800, fontSize: 14, color: "var(--ink)" }}>{title}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: color, marginTop: 2 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 2 }}>{sub}</div>}
      </div>
      {action && (
        <a
          href={action}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 12, fontWeight: 600, color: color, textDecoration: "none",
            marginTop: 2,
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = ".75"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          {actionLabel} <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

export function PageSupport() {
  const { t } = useLang();
  const [messageSent, setMessageSent] = useState(false);
  const [msg, setMsg] = useState({ subject: "", body: "" });

  const handleSend = () => {
    if (!msg.subject.trim() || !msg.body.trim()) return;
    setMessageSent(true);
    setMsg({ subject: "", body: "" });
    setTimeout(() => setMessageSent(false), 5000);
  };

  const FAQS = [
    { q: t("support.faq.q1"), a: t("support.faq.a1") },
    { q: t("support.faq.q2"), a: t("support.faq.a2") },
    { q: t("support.faq.q3"), a: t("support.faq.a3") },
    { q: t("support.faq.q4"), a: t("support.faq.a4") },
    { q: t("support.faq.q5"), a: t("support.faq.a5") },
    { q: t("support.faq.q6"), a: t("support.faq.a6") },
    { q: t("support.faq.q7"), a: t("support.faq.a7") },
    { q: t("support.faq.q8"), a: t("support.faq.a8") },
  ];

  return (
    <div style={{ padding: "28px 32px", maxWidth: 900, margin: "0 auto" }}>
      <PageHero
        icon={LifeBuoy}
        color="#f59e0b"
        title={t("support.title")}
        subtitle={t("support.subtitle")}
      />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
        <InfoBadge icon={Clock}      label={t("legal.responseTime")} value={t("legal.responseVal")} color="var(--teal)"  />
        <InfoBadge icon={Headphones} label={t("legal.supportHours")} value={t("legal.hoursVal")}    color="var(--blue)"  />
        <InfoBadge icon={Zap}        label={t("legal.priority")}     value={t("legal.priorityVal")} color="var(--amber)" />
      </div>

      {/* Contact cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14, marginBottom: 28 }}>
        <ContactCard
          icon={Mail}
          title={t("support.email.title")}
          value={t("support.email.val")}
          sub={t("support.email.sub")}
          color="var(--teal)"
          action="mailto:support@kungabasics.com"
          actionLabel={t("support.email.action")}
        />
        <ContactCard
          icon={MessageSquare}
          title={t("support.tech.title")}
          value={t("support.tech.val")}
          sub={t("support.tech.sub")}
          color="var(--blue)"
          action="mailto:tech@kungabasics.com"
          actionLabel={t("support.tech.action")}
        />
        <ContactCard
          icon={BookOpen}
          title={t("support.docs.title")}
          value={t("support.docs.val")}
          sub={t("support.docs.sub")}
          color="var(--amber)"
          action="#"
          actionLabel={t("support.docs.action")}
        />
      </div>

      {/* FAQ */}
      <Section title={t("support.faqTitle")} icon={FileQuestion} color="var(--teal)">
        {FAQS.map((faq, i) => (
          <FaqItem key={i} q={faq.q} a={faq.a} />
        ))}
      </Section>

      {/* Contact form */}
      <Section title={t("support.formTitle")} icon={Mail} color="var(--blue)">
        {messageSent ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            padding: "28px 16px", gap: 10, textAlign: "center",
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: "rgba(13,184,126,.12)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CheckCircle2 size={26} color="var(--teal)" />
            </div>
            <div style={{ fontFamily: "var(--fd)", fontWeight: 800, fontSize: 16, color: "var(--ink)" }}>
              {t("support.sent.title")}
            </div>
            <div style={{ fontSize: 13, color: "var(--ghost)", maxWidth: 340 }}>
              {t("support.sent.body")}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", display: "block", marginBottom: 6 }}>
                {t("support.form.subject")}
              </label>
              <input
                type="text"
                placeholder={t("support.form.subjectHint")}
                value={msg.subject}
                onChange={e => setMsg(m => ({ ...m, subject: e.target.value }))}
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "10px 14px", borderRadius: 10,
                  border: "1px solid var(--border)", background: "var(--panel)",
                  color: "var(--ink)", fontSize: 13, outline: "none",
                  fontFamily: "var(--fm)", transition: "border-color .15s",
                }}
                onFocus={e => e.target.style.borderColor = "var(--teal)"}
                onBlur={e => e.target.style.borderColor = "var(--border)"}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", display: "block", marginBottom: 6 }}>
                {t("support.form.message")}
              </label>
              <textarea
                rows={5}
                placeholder={t("support.form.messageHint")}
                value={msg.body}
                onChange={e => setMsg(m => ({ ...m, body: e.target.value }))}
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "10px 14px", borderRadius: 10,
                  border: "1px solid var(--border)", background: "var(--panel)",
                  color: "var(--ink)", fontSize: 13, outline: "none",
                  fontFamily: "var(--fm)", resize: "vertical", lineHeight: 1.6,
                  transition: "border-color .15s",
                }}
                onFocus={e => e.target.style.borderColor = "var(--teal)"}
                onBlur={e => e.target.style.borderColor = "var(--border)"}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={handleSend}
                disabled={!msg.subject.trim() || !msg.body.trim()}
                style={{
                  padding: "10px 24px", borderRadius: 10,
                  background: (!msg.subject.trim() || !msg.body.trim())
                    ? "var(--border)"
                    : "linear-gradient(135deg, var(--teal) 0%, #086b47 100%)",
                  color: (!msg.subject.trim() || !msg.body.trim()) ? "var(--ghost)" : "#fff",
                  border: "none", cursor: (!msg.subject.trim() || !msg.body.trim()) ? "not-allowed" : "pointer",
                  fontFamily: "var(--fd)", fontWeight: 700, fontSize: 13,
                  transition: "opacity .15s",
                }}
                onMouseEnter={e => { if (!(!msg.subject.trim() || !msg.body.trim())) e.currentTarget.style.opacity = ".9"; }}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
              >
                {t("support.form.send")}
              </button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
